import { useNavigate, useLocation } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import {
  Ship,
  Package,
  Anchor,
  Eye,
  History,
  Train,
  Plane,
} from "lucide-react";
import UnifiedContainerTracker from "../components/UnifiedContainerTracker";
import LCLTrackingContent from "./tracking/LCLTrackingContent";
import PortTrackingContent from "./tracking/PortTrackingContent";
import WatchlistContent from "./tracking/WatchlistContent";
import TrackingHistoryContent from "./tracking/TrackingHistoryContent";
import MetransTrackingContent from "./tracking/MetransTrackingContent";
import AirCargoTrackingContent from "./tracking/AirCargoTrackingContent";
import VesselTrackingContent from "./tracking/VesselTrackingContent";
import { useAuth } from "../context/AuthContext";
import { Navigation } from "lucide-react";

// Tabs for the Tracking Module
const TRACKING_TABS = [
  { id: "fcl", label: "FCL Tracking", icon: Ship, path: "/tracking" },
  { id: "lcl", label: "LCL / BL", icon: Package, path: "/tracking/lcl" },
  { id: "air", label: "Air Cargo", icon: Plane, path: "/tracking/air" },
  { id: "vessel", label: "Vessel", icon: Navigation, path: "/tracking/vessel" },
  { id: "port", label: "Port Tracking", icon: Anchor, path: "/tracking/port" },
  { id: "metrans", label: "Metrans Rail", icon: Train, path: "/tracking/metrans" },
  { id: "watchlist", label: "My Watchlist", icon: Eye, path: "/tracking/watchlist" },
  { id: "history", label: "History", icon: History, path: "/tracking/history" },
];

export default function TrackingModulePage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab based on current path
  const getActiveTab = () => {
    if (location.pathname.includes("/tracking/lcl")) return "lcl";
    if (location.pathname.includes("/tracking/air")) return "air";
    if (location.pathname.includes("/tracking/vessel")) return "vessel";
    if (location.pathname.includes("/tracking/port")) return "port";
    if (location.pathname.includes("/tracking/metrans")) return "metrans";
    if (location.pathname.includes("/tracking/watchlist")) return "watchlist";
    if (location.pathname.includes("/tracking/history")) return "history";
    return "fcl";
  };

  const activeTab = getActiveTab();
  const { getAuthHeaders, isEuropacificUser, user } = useAuth();

  // Render the appropriate content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case "lcl":
        return <LCLTrackingContent />;
      case "air":
        return <AirCargoTrackingContent />;
      case "vessel":
        return <VesselTrackingContent />;
      case "port":
        return <PortTrackingContent />;
      case "metrans":
        return <MetransTrackingContent />;
      case "watchlist":
        return <WatchlistContent />;
      case "history":
        return <TrackingHistoryContent onTrackAgain={(containerNum) => {
          navigate("/tracking");
          // Trigger search in UnifiedContainerTracker - will need to implement
        }} />;
      case "fcl":
      default:
        return (
          <div className="max-w-[1800px] mx-auto p-6">
            <UnifiedContainerTracker 
              getAuthHeaders={getAuthHeaders} 
              isEuropacificUser={isEuropacificUser} 
              user={user} 
            />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Navbar />
      
      {/* Spacer for fixed navbar */}
      <div className="h-14"></div>
      
      {/* Tracking Module Header with Tabs - Fixed below navbar */}
      <div className="bg-white border-b sticky top-14 z-40">
        <div className="max-w-[1800px] mx-auto px-6">
          <div className="flex items-center justify-between py-4">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Tracking Module</h1>
              <p className="text-sm text-muted-foreground">Track containers, shipments, and port schedules</p>
            </div>
            <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
              {TRACKING_TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => navigate(tab.path)}
                    data-testid={`tracking-tab-${tab.id}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      activeTab === tab.id
                        ? "bg-white text-primary shadow-sm"
                        : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content Area - renders the active tab's content */}
      <div className="tracking-module-content">
        {renderContent()}
      </div>
    </div>
  );
}
